import { call, put, select, take } from '@redux-saga/core/effects';

import { getAuthState } from '../../features/auth/authSlice';
import { modalsActions } from '../../features/modals/modalsSlice';
import {
  createEntity,
  deleteEntity,
  fetchDataById,
  fetchDatas,
  updateEntity,
} from '../../services/axiosInstanceService';
import { NotificationStatus } from '../../utils/enums';
import { appManagerActions } from '../appManager/actions';
import { REQUEST } from '../common/actions';
import onErrorLogout, { historyRedirect } from '../common/sagaCommon';
import { loaderActions } from '../loader/actions';
import { getPropertyTypologiesState } from '../propertyTypologies/reducer';
import {
  CREATE_COMPARATIVE_STUDY,
  DELETE_COMPARATIVE_STUDY,
  FETCH_ACTIVE_COMPARATIVE_STUDY,
  FETCH_COMPARATIVE_STUDIES,
  UPDATE_COMPARATIVE_STUDY,
  comparativeStudiesActions,
} from './actions';
import { comparativeStudiesNotifications } from './notifications';
import {
  comparativeStudiesApiToStore,
  comparativeStudyApiToStore,
  comparativeStudyFormToApi,
} from './parser';
import { getComparativeStudiesState } from './reducer';

export function* handleFetchComparativeStudies() {
  while (true) {
    try {
      yield take(FETCH_COMPARATIVE_STUDIES[REQUEST]);

      const { propertyTypologies } = yield select(getPropertyTypologiesState);
      const { userIdIri } = yield select(getAuthState);
      const response = yield call(fetchDatas, `${userIdIri}/comparative-studies`);
      if (response) {
        const parsedResponse = comparativeStudiesApiToStore(
          response['hydra:member'],
          propertyTypologies,
        );

        yield put(
          comparativeStudiesActions.fetchComparativeStudies.success(parsedResponse),
        );
      }
    } catch (error) {
      yield onErrorLogout(error);
      yield put(comparativeStudiesActions.fetchComparativeStudies.failure(error));
    }
  }
}

export function* handleFetchComparativeStudy() {
  while (true) {
    try {
      yield take(FETCH_ACTIVE_COMPARATIVE_STUDY[REQUEST]);

      const { comparativeStudyActive } = yield select(getComparativeStudiesState);
      const { propertyTypologies } = yield select(getPropertyTypologiesState);
      const response = yield call(
        fetchDataById,
        `/comparative-studies/${comparativeStudyActive}`,
      );

      if (response) {
        const parsedResponse = comparativeStudyApiToStore(
          response,
          propertyTypologies,
        );
        yield put(
          comparativeStudiesActions.fetchActiveComparativeStudy.success(
            parsedResponse,
          ),
        );
      }
    } catch (error) {
      yield onErrorLogout(error);
      yield put(
        appManagerActions.actionNotificationDisplay.visible({
          title: 'Etude comparative',
          content: `Le résultat de l'étude comparative n'a pas pu être chargée`,
          status: NotificationStatus.FAILURE,
        }),
      );
      yield put(
        comparativeStudiesActions.fetchActiveComparativeStudy.failure(error),
      );
    }
  }
}

export function* handleCreateComparativeStudies() {
  while (true) {
    try {
      const {
        payload: { navigate, body },
      } = yield take(CREATE_COMPARATIVE_STUDY[REQUEST]);
      const { propertyTypologies } = yield select(getPropertyTypologiesState);
      const parsedBody = comparativeStudyFormToApi(body);

      yield put(loaderActions.loaderDisplay.visible());

      const response = yield call(createEntity, {
        endpoint: '/comparative-studies',
        body: parsedBody,
      });
      if (response) {
        const parsedResponse = comparativeStudyApiToStore(
          response,
          propertyTypologies,
        );
        yield put(
          comparativeStudiesActions.createComparativeStudy.success(parsedResponse),
        );
        yield put(loaderActions.loaderDisplay.hidden());
        yield call(historyRedirect, navigate, '/etudes-comparatives');

        // create notification
        yield put(
          appManagerActions.actionNotificationDisplay.visible(
            comparativeStudiesNotifications.create().success(),
          ),
        );
      }
    } catch (error) {
      yield put(
        appManagerActions.actionNotificationDisplay.visible(
          comparativeStudiesNotifications.create().failure(),
        ),
      );
      yield put(comparativeStudiesActions.createComparativeStudy.failure(error));
      yield put(loaderActions.loaderDisplay.hidden());
      yield onErrorLogout(error);
    }
  }
}

export function* handleUpdateComparativeStudies() {
  while (true) {
    try {
      const {
        payload: { navigate, idIri, body },
      } = yield take(UPDATE_COMPARATIVE_STUDY[REQUEST]);
      const { propertyTypologies } = yield select(getPropertyTypologiesState);
      const parsedBody = comparativeStudyFormToApi(body);

      yield put(loaderActions.loaderDisplay.visible());
      const response = yield call(updateEntity, { idIri, body: parsedBody });

      if (response) {
        yield call(historyRedirect, navigate, '/etudes-comparatives');
        const parsedResponse = comparativeStudyApiToStore(
          response,
          propertyTypologies,
        );

        yield put(
          comparativeStudiesActions.updateComparativeStudy.success(parsedResponse),
        );
        yield put(
          appManagerActions.actionNotificationDisplay.visible(
            comparativeStudiesNotifications.update().success(),
          ),
        );
        yield put(loaderActions.loaderDisplay.hidden());
      }
    } catch (error) {
      yield put(
        appManagerActions.actionNotificationDisplay.visible(
          comparativeStudiesNotifications.update().failure(),
        ),
      );
      yield onErrorLogout(error);
      yield put(loaderActions.loaderDisplay.hidden());
      yield put(comparativeStudiesActions.updateComparativeStudy.failure(error));
    }
  }
}

export function* handleDeleteComparativeStudies() {
  while (true) {
    try {
      const { payload } = yield take(DELETE_COMPARATIVE_STUDY[REQUEST]);

      yield put(loaderActions.loaderDisplay.visible());
      const status = yield call(deleteEntity, payload);

      if (status === 204) {
        yield put(comparativeStudiesActions.deleteComparativeStudy.success(payload));
        yield put(modalsActions.deleteComparativeStudy(null));
        yield put(loaderActions.loaderDisplay.hidden());
        yield put(
          appManagerActions.actionNotificationDisplay.visible(
            comparativeStudiesNotifications.delete().success(),
          ),
        );
      }
    } catch (error) {
      yield onErrorLogout(error);
      yield put(
        appManagerActions.actionNotificationDisplay.visible(
          comparativeStudiesNotifications.delete().failure(),
        ),
      );
      yield put(comparativeStudiesActions.deleteComparativeStudy.failure(error));
      yield put(loaderActions.loaderDisplay.hidden());
    }
  }
}
